import Decimal from 'decimal.js';
import { ERROR_MESSAGES } from './messages';

/**
 * タグのフォーマットを表す列挙型です。
 */
enum TagFormat {
  NUMBER = 1,
  STRING = 2,
  DATE = 3,
}

export const INVALID_TAG_LABELS = [
  '文書名', '文書種類', 'ステータス', 'PDFファイル名', 'サイズ', '契約種別', '書面有無', '文書ID', 'バーコード印字欄', '保管品バーコード',
  '登録者', '登録日時', '更新者', '更新日時', '検索結果', 'メモ',
] as const;

// eslint-disable-next-line @typescript-eslint/no-namespace
namespace TagFormat {

  /**
   * TagFormatに対応するエラーメッセージを返します。
   *
   * @param tagFormat - TagFormat
   * @param formatCheck - number
   * @returns - エラーメッセージ
   */
  export function getInvalidErrorMessage(tagFormat: TagFormat, formatCheck: number | undefined, value: string): string {
    switch (tagFormat) {
      case TagFormat.NUMBER: {
        // Check if the error is due to exceeding digits
        const [beforePoint = '', afterPoint = ''] = value.replace(/^[-+]?/, '').split('.');
        if (beforePoint.length > 15 || afterPoint.length > 4) return ERROR_MESSAGES.LENGTH_ERROR;

        return ERROR_MESSAGES.NUMERIC_ERROR;
      }
      case TagFormat.STRING: {
        if (value.length > 1024) return ERROR_MESSAGES.LENGTH_ERROR;
        if (formatCheck) return ERROR_MESSAGES.ALPHANUMERIC_ERROR;
        return '';
      }
      default: {
        if (value.length > 1024) return ERROR_MESSAGES.LENGTH_ERROR;
        return '';
      }
    }
  }

  /**
   * TagFormatに対応するタイトルを返します。
   *
   * @param tagFormat - TagFormat
   * @returns - タイトル
   */
  export function getPlaceholder(tagFormat: TagFormat, formatCheck: number | undefined = undefined): string {
    switch (tagFormat) {
      case TagFormat.NUMBER: return '半角数字';
      case TagFormat.STRING: {
        if (formatCheck) return '半角英数記号';
        return 'テキスト';
      }
      case TagFormat.DATE: return 'YYYY/MM/DD';

      default:
        return '不明';
    }
  }

  /**
   * TagFormatと、タイトルをキー・バリュー形式で返します。
   *
   * @returns - TagFormatに対応する文字列マップを返す
   */
  export function getTextMap() {
    return { [TagFormat.STRING]: '文字列', [TagFormat.NUMBER]: '数値', [TagFormat.DATE]: '日付' };
  }

  /**
   * 特定のTagFormatに対応する値の検証を行い、適切な値を返します。
   *
   * @param tagFormat - TagFormat
   * @param formatCheck - フォーマットチェックの有無
   * @param value - 入力値
   * @returns -  検証済みの値を返す
   */
  export function validate(
    tagFormat: TagFormat,
    formatCheck: number | undefined,
    value: string,
  ): boolean {
    if (value && tagFormat === TagFormat.NUMBER) {
      const validNumberRegex = /^[-+]?(?:\d{1,15})(?:\.\d{1,4})?$/;
      return validNumberRegex.test(value);
    }
    if (value && tagFormat === TagFormat.STRING && formatCheck) {
      if (value.length > 1024) return false;

      const halfWidthAlphanumericRegex = /^[\x20-\x7E]+$/;
      return halfWidthAlphanumericRegex.test(value);
    }
    if (value && tagFormat === TagFormat.STRING) {
      if (value.length > 1024) return false;
    }
    if (value && tagFormat === TagFormat.DATE) {
      const regex = /^\d{4}\/\d{2}\/\d{2}$/;
      return regex.test(value);
    }
    return true;
  }

  /**
   * タグ名の検証を行い、適切なエラーメッセージを返します。
   *
   * @param tagLabel - タグ名
   * @returns -  エラーメッセージ
   */
  export function validateTagLabel(
    tagLabel: string,
  ): string {
    const label = tagLabel.trim();
    if (!label) return 'タグ名を入力してください。';
    if (INVALID_TAG_LABELS.some((t) => t === label)) {
      return `「${label}」は標準項目のため登録できません。\n別のタグ名を入力してください。`;
    }
    return '';
  }

  /**
   * TagFormatに対応する型名を返します。
   *
   * @param tagType - TagFormat
   * @returns 型名を返す
   */
  export function getType(tagType: TagFormat): string {
    switch (tagType) {
      case TagFormat.NUMBER: return '数値';
      case TagFormat.STRING: return '文字列';
      case TagFormat.DATE: return '日付';
      default: return '不明';
    }
  }

  export function getValidErrorText(tagFormat: TagFormat): string {
    switch (tagFormat) {
      case TagFormat.NUMBER: return '値が不正です';
      case TagFormat.DATE: return '日付が不正です';
      default: return '不明';
    }
  }

  export function normalizeTagValue(tagFormat: TagFormat, value: string): string {
    if (tagFormat === TagFormat.NUMBER && value) {
      const decimal = new Decimal(value);
      return decimal.toFixed();
    }
    return value;
  }
}

export default TagFormat;
